import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Container } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiDownload,
  FiFileText,
  FiHome,
  FiList,
  FiSend,
  FiUploadCloud,
  FiX,
  FiXCircle,
} from 'react-icons/fi';

import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import BasePage from '../../../components/BasePage';
import CompanyPickerModal from './CompanyPickerModal';

import { parseBatchFile, ParseResult } from './parser';
import {
  Alert,
  BackLink,
  Button,
  Card,
  DeclarationRow,
  DownloadButton,
  Dropzone,
  ErrorList,
  FileBadge,
  PageContainer,
  PageHeader,
  ResultBlock,
  ReviewRow,
  ReviewTable,
  ReviewTableBody,
  ReviewTableHeader,
  SectionTitle,
  SelectWrapper,
  StepFooter,
  Stepper,
  StepItem,
  SummaryRow,
  SummaryTile,
  TemplateBox,
} from './styles';

interface ISelectOption {
  value: string;
  label: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

interface ICompany {
  id: string;
  nome_fantasia: string;
  cnpj?: string;
}

const TEMPLATE_URL =
  'https://vivavoz.s3.us-east-1.amazonaws.com/modelo_pre_cadastro.xlsx';

const ACTIVITY_LABELS: Record<string, string> = {
  industrial: 'Industrial',
  rural: 'Rural',
  market: 'Comércio',
  'public-service': 'Serviço Público',
};

const GENDER_LABELS: Record<string, string> = {
  male: 'Masculino',
  female: 'Feminino',
  other: 'Outro',
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BatchWorkers: React.FC = () => {
  const history = useHistory();
  const { user, syndicate } = useAuth();
  const isSyndicateUser = user?.role === 'syndicate';

  const [unions, setUnions] = useState<ISelectOption[]>([]);
  const [union, setUnion] = useState<string | null>(
    isSyndicateUser ? syndicate?.id ?? null : null,
  );

  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [company, setCompany] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [declared, setDeclared] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [resultState, setResultState] = useState<{
    tone: 'success' | 'danger';
    message: string;
    errors: string[];
  } | null>(null);

  const [stepIdx, setStepIdx] = useState(isSyndicateUser ? 1 : 0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const STEPS = useMemo(
    () => [
      { key: 'syndicate', label: 'Sindicato' },
      { key: 'upload', label: 'Arquivo' },
      { key: 'review', label: 'Revisão' },
      { key: 'result', label: 'Resultado' },
    ],
    [],
  );

  useEffect(() => {
    document.title = 'Cadastro em Lote - Viva Voz';
  }, []);

  useEffect(() => {
    if (isSyndicateUser) return undefined;
    let mounted = true;
    api.get<ISyndicate[]>('/unions').then(response => {
      if (!mounted) return;
      setUnions(
        response.data.map(s => ({ value: s.id, label: s.nome_fantasia })),
      );
    });
    return () => {
      mounted = false;
    };
  }, [isSyndicateUser]);

  const syndicateId = isSyndicateUser ? syndicate?.id : union;

  useEffect(() => {
    if (!syndicateId) {
      setCompanies([]);
      setCompany('');
      setCompanyName('');
      return;
    }
    let mounted = true;
    api
      .get<ICompany[]>('/companies', {
        params: { syndicate_id: syndicateId, limit: 9999 },
      })
      .then(response => {
        if (!mounted) return;
        setCompanies(response.data);
        setCompany('');
        setCompanyName('');
      });
    // eslint-disable-next-line consistent-return
    return () => {
      mounted = false;
    };
  }, [syndicateId]);

  const handleFileSelect = useCallback(async (picked: File) => {
    if (!picked.name.toLowerCase().endsWith('.xlsx')) {
      setParseError('Apenas arquivos .xlsx são aceitos.');
      setFile(null);
      setParseResult(null);
      return;
    }
    if (picked.size > 25 * 1024 * 1024) {
      setParseError('O arquivo excede o limite de 25 MB.');
      setFile(null);
      setParseResult(null);
      return;
    }

    setFile(picked);
    setParseError(null);
    setParseResult(null);
    setParsing(true);
    try {
      const result = await parseBatchFile(picked);
      setParseResult(result);
    } catch (err) {
      setParseError(
        'Não foi possível ler o arquivo. Verifique se o formato é válido.',
      );
    } finally {
      setParsing(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) handleFileSelect(picked);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const picked = e.dataTransfer.files?.[0];
    if (picked) handleFileSelect(picked);
  };

  const resetFile = () => {
    setFile(null);
    setParseResult(null);
    setParseError(null);
  };

  const handleSubmit = async () => {
    if (!file || !union) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append('file', file);
    if (company) formData.append('company_id', company);

    try {
      const response = await api.post(
        `/workers/bulk-pre-registration/${union}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const { result } = response.data;
      const apiErrors: string[] = result?.errors || [];
      setResultState({
        tone: apiErrors.length > 0 ? 'danger' : 'success',
        message: result?.message || 'Importação concluída',
        errors: apiErrors,
      });
    } catch (err) {
      setResultState({
        tone: 'danger',
        message: 'Erro ao processar o arquivo no servidor.',
        errors: [],
      });
    } finally {
      setSubmitting(false);
      setStepIdx(3);
    }
  };

  const restartFlow = () => {
    setFile(null);
    setParseResult(null);
    setParseError(null);
    setDeclared(false);
    setResultState(null);
    setCompany('');
    setCompanyName('');
    setStepIdx(isSyndicateUser ? 1 : 0);
  };

  /* ─── Derived values ─── */

  const canGoNextFromSyndicate = !!union;
  const canGoNextFromUpload =
    !!file &&
    !parsing &&
    !parseError &&
    !!parseResult &&
    parseResult.fileErrors.length === 0 &&
    parseResult.totalRows > 0;
  const canSubmit =
    declared && !!parseResult && parseResult.validRows > 0 && !submitting;

  const unionOption = useMemo(() => {
    if (isSyndicateUser && syndicate) {
      return { value: syndicate.id, label: syndicate.name };
    }
    return unions.find(u => u.value === union) ?? null;
  }, [unions, union, isSyndicateUser, syndicate]);

  /* ─── Step renderers ─── */

  const renderSyndicateStep = () => (
    <Card>
      <SectionTitle>
        <h2>Escolha o sindicato</h2>
        <span className="subtitle">
          Os trabalhadores importados serão vinculados ao sindicato selecionado.
        </span>
      </SectionTitle>

      <SelectWrapper>
        <Select
          options={unions}
          placeholder="Selecione um sindicato"
          value={unionOption}
          classNamePrefix="react-select"
          onChange={opt => setUnion(opt?.value ?? null)}
          isClearable
          isSearchable
          noOptionsMessage={() => 'Nenhum sindicato encontrado'}
        />
      </SelectWrapper>

      <StepFooter>
        <span className="left">
          <FiHome size={12} />
          {unions.length}
          {' sindicatos disponíveis'}
        </span>
        <div className="right">
          <Button
            $variant="secondary"
            type="button"
            onClick={() => history.push('/workers')}
          >
            <FiArrowLeft size={14} />
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!canGoNextFromSyndicate}
            onClick={() => setStepIdx(1)}
          >
            Continuar
            <FiArrowRight size={14} />
          </Button>
        </div>
      </StepFooter>
    </Card>
  );

  const renderUploadStep = () => (
    <Card>
      <SectionTitle>
        <h2>Envie o arquivo de importação</h2>
        <span className="subtitle">
          {'Formatos aceitos: '}
          <strong>.xlsx</strong>
          {' · até 25 MB. Use o modelo oficial para evitar erros.'}
        </span>
      </SectionTitle>

      {companies.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: '#475569',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: 6,
            }}
          >
            Empresa (opcional)
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '10px 14px',
              border: '1.5px solid #e2e8f0',
              borderRadius: 12,
              background: '#f8fafc',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 14,
              fontFamily: 'inherit',
              color: company ? '#0f172a' : '#94a3b8',
              transition: 'border-color 0.15s ease',
            }}
          >
            <FiHome size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              {companyName || 'Associar todos a uma empresa...'}
            </span>
            {company && (
              <FiX
                size={14}
                style={{ color: '#94a3b8', flexShrink: 0 }}
                onClick={e => {
                  e.stopPropagation();
                  setCompany('');
                  setCompanyName('');
                }}
              />
            )}
          </button>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            Todos os trabalhadores do lote serão vinculados a esta empresa.
          </div>
        </div>
      )}

      <TemplateBox>
        <span className="icon">
          <FiFileText size={18} />
        </span>
        <div className="info">
          <div className="title">Modelo oficial de importação</div>
          <div className="hint">
            Baixe, preencha com os dados dos trabalhadores e envie aqui.
          </div>
        </div>
        <DownloadButton
          href={TEMPLATE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FiDownload size={14} />
          Baixar modelo
        </DownloadButton>
      </TemplateBox>

      {!file ? (
        <Dropzone
          $active={dragging}
          onDragEnter={() => setDragging(true)}
          onDragOver={e => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <div className="icon-wrap">
            <FiUploadCloud size={26} />
          </div>
          <div className="title">Arraste seu arquivo aqui</div>
          <div className="hint">
            {'ou '}
            <span className="browse">clique para escolher</span>
            {' do seu computador'}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
        </Dropzone>
      ) : (
        <FileBadge>
          <span className="icon">
            <FiFileText size={16} />
          </span>
          <div className="info">
            <span className="name" title={file.name}>
              {file.name}
            </span>
            <span className="size">
              {formatBytes(file.size)}
              {parsing && ' · analisando...'}
              {!parsing &&
                parseResult &&
                ` · ${parseResult.totalRows} linhas lidas`}
            </span>
          </div>
          <button
            type="button"
            className="remove"
            onClick={resetFile}
            aria-label="Remover arquivo"
          >
            <FiX size={16} />
          </button>
        </FileBadge>
      )}

      {parseError && (
        <Alert $tone="danger">
          <FiAlertCircle className="icon" size={16} />
          <div className="body">{`Arquivo inválido. ${parseError}`}</div>
        </Alert>
      )}

      {parseResult && parseResult.fileErrors.length > 0 && (
        <Alert $tone="danger">
          <FiAlertCircle className="icon" size={16} />
          <div className="body">
            <strong>Problemas no arquivo:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {parseResult.fileErrors.map(err => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        </Alert>
      )}

      {parseResult &&
        parseResult.fileErrors.length === 0 &&
        parseResult.totalRows > 0 && (
          <Alert $tone="success">
            <FiCheckCircle className="icon" size={16} />
            <div className="body">
              <strong>Arquivo lido com sucesso.</strong>
              {' Encontramos '}
              {parseResult.totalRows}
              {' linhas — continue para revisar os dados.'}
            </div>
          </Alert>
        )}

      <StepFooter>
        <span className="left">
          <FiUploadCloud size={12} />
          Apenas planilhas Excel (.xlsx)
        </span>
        <div className="right">
          {!isSyndicateUser && (
            <Button
              $variant="secondary"
              type="button"
              onClick={() => setStepIdx(0)}
            >
              <FiArrowLeft size={14} />
              Voltar
            </Button>
          )}
          <Button
            type="button"
            disabled={!canGoNextFromUpload}
            onClick={() => setStepIdx(2)}
          >
            Revisar dados
            <FiArrowRight size={14} />
          </Button>
        </div>
      </StepFooter>
    </Card>
  );

  const renderReviewStep = () => {
    if (!parseResult) return null;

    return (
      <Card>
        <SectionTitle>
          <h2>Revise antes de enviar</h2>
          <span className="subtitle">
            Linhas com problemas são destacadas — corrija a planilha e reenvie,
            ou prossiga apenas com as linhas válidas.
          </span>
        </SectionTitle>

        <SummaryRow>
          <SummaryTile $tone="neutral">
            <span className="icon">
              <FiList size={16} />
            </span>
            <div>
              <div className="value">{parseResult.totalRows}</div>
              <div className="label">Linhas lidas</div>
            </div>
          </SummaryTile>
          <SummaryTile $tone="success">
            <span className="icon">
              <FiCheckCircle size={16} />
            </span>
            <div>
              <div className="value">{parseResult.validRows}</div>
              <div className="label">Válidas</div>
            </div>
          </SummaryTile>
          <SummaryTile $tone="danger">
            <span className="icon">
              <FiXCircle size={16} />
            </span>
            <div>
              <div className="value">{parseResult.invalidRows}</div>
              <div className="label">Com problemas</div>
            </div>
          </SummaryTile>
          <SummaryTile $tone="warning">
            <span className="icon">
              <FiSend size={16} />
            </span>
            <div>
              <div className="value">{parseResult.validRows}</div>
              <div className="label">Serão enviadas</div>
            </div>
          </SummaryTile>
        </SummaryRow>

        {parseResult.invalidRows > 0 && (
          <Alert $tone="warning">
            <FiAlertCircle className="icon" size={16} />
            <div className="body">
              <strong>
                {parseResult.invalidRows}
                {' linhas com problemas. '}
              </strong>
              O servidor também validará os dados e pode rejeitar linhas extras
              (ex.: CPF já cadastrado). Corrija o que puder na planilha para
              reduzir falhas.
            </div>
          </Alert>
        )}

        <ReviewTable>
          <ReviewTableHeader>
            <span>Linha</span>
            <span>Nome</span>
            <span>CPF</span>
            <span>Telefone</span>
            <span>Perfil</span>
            <span>Gênero</span>
            <span>Status</span>
          </ReviewTableHeader>
          <ReviewTableBody>
            {parseResult.rows.map(row => {
              const invalid = row.errors.length > 0;
              return (
                <ReviewRow key={row.rowNumber} $invalid={invalid}>
                  <span className="rowNum">{`#${row.rowNumber}`}</span>
                  <span
                    className={row.nome ? 'cell' : 'cell muted'}
                    title={row.nome}
                  >
                    {row.nome || '—'}
                  </span>
                  <span className={row.cpf ? 'cell' : 'cell muted'}>
                    {row.cpf || '—'}
                  </span>
                  <span className={row.telefone ? 'cell' : 'cell muted'}>
                    {row.telefone || '—'}
                  </span>
                  <span className={row.perfil ? 'cell' : 'cell muted'}>
                    {row.perfil ? ACTIVITY_LABELS[row.perfil] : 'Rural'}
                  </span>
                  <span className={row.genero ? 'cell' : 'cell muted'}>
                    {row.genero ? GENDER_LABELS[row.genero] : 'Outro'}
                  </span>
                  <div className="errors">
                    {invalid ? (
                      row.errors.map(err => (
                        <span key={err} className="error-chip" title={err}>
                          <FiAlertCircle size={10} />
                          {err}
                        </span>
                      ))
                    ) : (
                      <span className="ok-chip">
                        <FiCheck size={10} />
                        Válido
                      </span>
                    )}
                  </div>
                </ReviewRow>
              );
            })}
          </ReviewTableBody>
        </ReviewTable>

        <DeclarationRow>
          <input
            type="checkbox"
            checked={declared}
            onChange={e => setDeclared(e.target.checked)}
          />
          <span className="text">
            {'Declaro que os dados aqui importados são '}
            <strong>fidedignos</strong>
            {' e de responsabilidade desta entidade sindical.'}
          </span>
        </DeclarationRow>

        <StepFooter>
          <span className="left">
            {parseResult.validRows === 0 ? (
              <>
                <FiXCircle size={12} />
                Nenhuma linha válida para enviar
              </>
            ) : (
              <>
                <FiCheckCircle size={12} />
                {parseResult.validRows}
                {' trabalhadores prontos para envio'}
              </>
            )}
          </span>
          <div className="right">
            <Button
              $variant="secondary"
              type="button"
              onClick={() => setStepIdx(1)}
              disabled={submitting}
            >
              <FiArrowLeft size={14} />
              Trocar arquivo
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? (
                'Enviando...'
              ) : (
                <>
                  <FiSend size={14} />
                  {'Enviar '}
                  {parseResult.validRows}
                  {' registros'}
                </>
              )}
            </Button>
          </div>
        </StepFooter>
      </Card>
    );
  };

  const renderResultStep = () => {
    if (!resultState) return null;

    return (
      <Card>
        <ResultBlock $tone={resultState.tone}>
          <div className="icon-wrap">
            {resultState.tone === 'success' ? (
              <FiCheckCircle size={32} />
            ) : (
              <FiAlertCircle size={32} />
            )}
          </div>
          <h2>
            {resultState.tone === 'success'
              ? 'Importação concluída'
              : 'Importação com pendências'}
          </h2>
          <p>{resultState.message}</p>
        </ResultBlock>

        {resultState.errors.length > 0 && (
          <>
            <SectionTitle>
              <h2>Erros retornados pelo servidor</h2>
              <span className="subtitle">
                {resultState.errors.length}
                {' registros não foram importados.'}
              </span>
            </SectionTitle>
            <ErrorList>
              {resultState.errors.map(err => (
                <li key={err}>{err}</li>
              ))}
            </ErrorList>
          </>
        )}

        <StepFooter>
          <span className="left" />
          <div className="right">
            <Button $variant="secondary" type="button" onClick={restartFlow}>
              <FiUploadCloud size={14} />
              Enviar outra planilha
            </Button>
            <Button type="button" onClick={() => history.push('/workers')}>
              Ir para trabalhadores
              <FiArrowRight size={14} />
            </Button>
          </div>
        </StepFooter>
      </Card>
    );
  };

  const renderStep = () => {
    if (stepIdx === 0) return renderSyndicateStep();
    if (stepIdx === 1) return renderUploadStep();
    if (stepIdx === 2) return renderReviewStep();
    return renderResultStep();
  };

  return (
    <BasePage title="Cadastro em Lote">
      {pickerOpen && (
        <CompanyPickerModal
          companies={companies}
          selectedId={company}
          onConfirm={(id, name) => {
            setCompany(id);
            setCompanyName(name);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      <Container fluid>
        <PageContainer>
          <div>
            <BackLink type="button" onClick={() => history.push('/workers')}>
              <FiArrowLeft size={14} />
              Voltar para trabalhadores
            </BackLink>
            <PageHeader>
              <div className="titles">
                <h1>Cadastro em Lote</h1>
                <span className="subtitle">
                  Importe vários trabalhadores de uma só vez usando uma planilha
                  Excel.
                </span>
              </div>
            </PageHeader>
          </div>

          <Stepper>
            {STEPS.map((step, idx) => {
              let state: 'done' | 'active' | 'idle' = 'idle';
              if (idx < stepIdx) state = 'done';
              else if (idx === stepIdx) state = 'active';
              return (
                <StepItem key={step.key} $state={state}>
                  <span className="indicator">
                    {state === 'done' ? <FiCheck size={14} /> : idx + 1}
                  </span>
                  <div className="label">
                    <span className="index">
                      Passo
                      {idx + 1}
                    </span>
                    <span className="name">{step.label}</span>
                  </div>
                </StepItem>
              );
            })}
          </Stepper>

          {renderStep()}
        </PageContainer>
      </Container>
    </BasePage>
  );
};

export default BatchWorkers;
